import "leaflet/dist/leaflet.css";
import React, { useEffect, useState, useRef } from "react";
import { getNachDerSchulePage } from "../lib/api";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Page, PageHeader, Section, SectionHead, IntroSplit, CardGrid, MediaCard } from "../components/ui";
import "../styles/NachmittagsbetreuungPage.css";

// Fix Leaflet default marker icon broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom accent-colored pin for Horte
const accentIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 9.33 14 26 14 26S28 23.33 28 14C28 6.27 21.73 0 14 0z" fill="#925a64"/>
      <circle cx="14" cy="14" r="6" fill="white"/>
    </svg>
  `)}`,
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -42],
});

// Distinct school icon — larger, darker, with a house/school shape
const schoolIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="50" viewBox="0 0 36 50">
      <path d="M18 0C8.06 0 0 8.06 0 18c0 12 18 32 18 32S36 30 36 18C36 8.06 27.94 0 18 0z" fill="#3d1f26"/>
      <g transform="translate(9,8)" fill="white">
        <path d="M9 0L18 6v1H0V6L9 0z"/>
        <rect x="1" y="7" width="16" height="10" rx="1"/>
        <rect x="6" y="10" width="4" height="7" rx="1" fill="#3d1f26"/>
        <rect x="11" y="10" width="4" height="4" rx="1" fill="#3d1f26"/>
      </g>
    </svg>
  `)}`,
  iconSize: [36, 50],
  iconAnchor: [18, 50],
  popupAnchor: [0, -52],
});

export default function NachmittagsbetreuungPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const markerRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    getNachDerSchulePage()
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    heroTitle,
    heroSubtitle,
    heroImage,
    introKicker,
    introTitle,
    introText,
    kurseTitle,
    kurseSubtitle,
    kurse,
    horteTitle,
    horteSubtitle,
    horte,
  } = data || {};

  const pins = (horte || []).filter((h) => h.lat && h.lng);
  const SCHOOL = { lat: 48.16143, lng: 16.32014 };
  const allPoints = [SCHOOL, ...pins];
  const centerLat = allPoints.reduce((s, h) => s + h.lat, 0) / allPoints.length;
  const centerLng = allPoints.reduce((s, h) => s + h.lng, 0) / allPoints.length;

  const handleHortClick = (h) => {
    const marker = markerRefs.current[h.id];
    if (marker) marker.openPopup();
  };

  return (
    <Page loading={!data} error={error}>
      <PageHeader
        parent={{ label: "Lernwelt", to: "/lernen-und-verstehen" }}
        current="Nach dem Unterricht"
        title={heroTitle}
        subtitle={heroSubtitle}
      />

      <Section intro>
        <IntroSplit image={heroImage} kicker={introKicker} title={introTitle} text={introText} />
      </Section>

      {kurse?.length > 0 && (
        <Section>
          <SectionHead title={kurseTitle} subtitle={kurseSubtitle} />
          <CardGrid cols={3}>
            {kurse.map((kurs, i) => (
              <MediaCard
                key={kurs.id ?? i}
                image={kurs.image}
                iconName={kurs.iconName}
                title={kurs.title}
                text={kurs.text}
                href={kurs.link}
                hrefLabel={`Website von ${kurs.title} öffnen`}
              />
            ))}
          </CardGrid>
        </Section>
      )}

      {/* Map + Hort list keep their own layout — this pairing of a
          Leaflet map with a linked list doesn't recur anywhere else
          on the site. */}
      <Section>
        <SectionHead title={horteTitle} subtitle={horteSubtitle} />

        <div className="nb-horte-grid">
          <div className="nb-map-card">
            <MapContainer
              center={[centerLat, centerLng]}
              zoom={14}
              scrollWheelZoom={false}
              className="nb-map"
              style={{ height: "clamp(300px, 38vw, 480px)", width: "100%", borderRadius: "calc(22px - 6px)" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[48.162450, 16.320673]} icon={schoolIcon}>
                <Popup>
                  <strong>Rothenburgschule</strong>
                  <br />
                  Rothenburgstraße 1, 1120 Wien
                </Popup>
              </Marker>
              {pins.map((h, i) => (
                <Marker
                  key={h.id ?? i}
                  position={[h.lat, h.lng]}
                  icon={accentIcon}
                  ref={(r) => {
                    if (r) markerRefs.current[h.id] = r;
                  }}
                >
                  <Popup>
                    <strong>{h.name}</strong>
                    {h.address ? (
                      <>
                        <br />
                        {h.address}
                      </>
                    ) : null}
                    {h.distance ? (
                      <>
                        <br />
                        <em>{h.distance}</em>
                      </>
                    ) : null}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <ul className="nb-horte-list">
            {(horte || []).map((h, i) => {
              const hasPin = h.lat && h.lng;
              return (
                <li
                  key={h.id ?? i}
                  className={`nb-hort-card ${hasPin ? "is-clickable" : ""}`}
                  onClick={hasPin ? () => handleHortClick(h) : undefined}
                  role={hasPin ? "button" : undefined}
                  tabIndex={hasPin ? 0 : undefined}
                  onKeyDown={hasPin ? (e) => e.key === "Enter" && handleHortClick(h) : undefined}
                >
                  <div className="nb-hort-card__top">
                    <h3>{h.name}</h3>
                    {h.distance ? <span className="nb-hort-badge">{h.distance}</span> : null}
                  </div>
                  {h.address ? <p className="nb-hort-address">{h.address}</p> : null}
                </li>
              );
            })}
          </ul>
        </div>
      </Section>
    </Page>
  );
}